// © Microsoft Corporation. All rights reserved.

import { ActionHandler, IActionPayload, IReducer } from "@msnews/experiences-redux";
import { ContentActionsToolbarActions, ContentActionsToolbarState, ToolbarPosition } from ".";
import { Status, ToolbarBreakpoints } from "./ContentActionsToolbar.constants";

import { DocumentData } from "./ContentActionsToolbar.state";

/**
 * Experience initial state.
 */
const initialState: ContentActionsToolbarState = {
    documentData: undefined,
    documentId: undefined,
    position: undefined,
    sharingError: undefined,
    staticPosition: undefined,
    status: undefined
};

/**
 * The reducer for the ContentActionsToolbar connector, and the ContentActionsToolbarState
 * @export
 * @class
 */
export class ContentActionsToolbarReducer implements IReducer<ContentActionsToolbarState> {
    /**
     * Update the state for the <experience> connector, based on the action being applied
     * @param state The previous state
     * @param actionPayload The action being applied
     */
    public reduce(state: ContentActionsToolbarState, actionPayload: IActionPayload): ContentActionsToolbarState {
        // Return the initial state for this experience, including the config.
        if (!state) {
            return initialState;
        }

        // Return the passed in state, if there is no action specified
        if (!actionPayload) {
            return state;
        }

        let newState: ContentActionsToolbarState;

        // handle actions
        ActionHandler.handleAction(actionPayload, ContentActionsToolbarActions.initializeToolbar, (breakpointIndex: number,
            isRSThreeDevice: boolean,
            documentId: string,
            staticPosition: ToolbarPosition) => {
            newState = initialState;
            newState.position = this.getToolbarPosition(staticPosition, isRSThreeDevice, breakpointIndex);
            newState.documentId = documentId;
            newState.staticPosition = staticPosition;
        });

        ActionHandler.handleAction(actionPayload, ContentActionsToolbarActions.ContentStatuReady, (status: Status) => {
            newState = {
                ...state,
                status
            };
        });

        ActionHandler.handleAction(actionPayload, ContentActionsToolbarActions.documentDataReady, (documentData: DocumentData) => {
            newState = {
                ...state,
                documentData
            };
        });

        ActionHandler.handleAction(actionPayload, ContentActionsToolbarActions.likeContent, () => {
            newState = {
                ...state,
                status: Status.Like
            };
        });

        ActionHandler.handleAction(actionPayload, ContentActionsToolbarActions.unlikeContent, () => {
            newState = {
                ...state,
                status: Status.None
            };
        });

        ActionHandler.handleAction(actionPayload, ContentActionsToolbarActions.dislikeContent, () => {
            newState = {
                ...state,
                status: Status.DisLike
            };
        });

        ActionHandler.handleAction(actionPayload, ContentActionsToolbarActions.undislikeContent, () => {
            newState = {
                ...state,
                status: Status.None
            };
        });

        ActionHandler.handleAction(actionPayload, ContentActionsToolbarActions.ViewportUpdate, (breakpointIndex: number, isRSThreeDevice: boolean) => {
            newState = {
                ...state,
                position: this.getToolbarPosition(state.staticPosition, isRSThreeDevice, breakpointIndex)
            };
        });

        ActionHandler.handleAction(actionPayload, ContentActionsToolbarActions.sharingToolbarError, (error: Error) => {
            newState = {
                ...state,
                sharingError: error
            };
        });

        return newState || state;
    }

    /**
     * Calculates current toolbar position
     * @param staticPosition Defines orientation for static position if single position. Undefined otherwise.
     * @param isRSThreeDevice Indicates if device is RSThree
     * @param breakpointIndex Current breakpoint index.
     * @returns Toolbar current position
     */
    private getToolbarPosition(staticPosition: ToolbarPosition, isRSThreeDevice: boolean, breakpointIndex: number): ToolbarPosition {
        if (staticPosition) {
            return staticPosition;
        } else {
            const currentBreakpointIndex = isRSThreeDevice ? ToolbarBreakpoints.Breakpoint3_2 : ToolbarBreakpoints.Breakpoint3_3;
            return breakpointIndex >= currentBreakpointIndex ? ToolbarPosition.vertical : ToolbarPosition.horizontal;
        }
    }
}